import { RELACIONES_REPRESENTANTE } from "../../../../../constants/salud-pymes-constants";
import { Country, HiringColumnNamesMap, HiringInsuredFieldsMap, InsuredData } from "./salud-pymes-insured-types";

export const hiringColumnNames: HiringColumnNamesMap = {
  NOMBRE: "name",
  "APELLIDO 1": "lastname1",
  "APELLIDO 2": "lastname2",
  "FECHA DE NACIMIENTO (DD/MM/YYYY)": "birthDate",
  "GENERO (M/H)": "gender",
  NACIONALIDAD: "nationality",
  "TIPO DOCU (NIF-NIE-PAS)": "docType",
  "Nº DE DOCU": "docNumber",
  CADUCIDAD: "passportDate",
  PROVINCIA: "province",
  POBLACION: "town",
  DOMICILIO: "address",
  "NUMERO/KM": "number",
  PORTAL: "portal",
  ESCALERA: "stairs",
  PISO: "floor",
  PUERTA: "door",
  "CODIGO POSTAL": "postalCode",
  "PREFIJO MOVIL": "prefix",
  MOVIL: "mobilePhone",
  EMAIL: "email",
  "CUENTA COPAGO": "copay",
  "GS30 - DENTAL - (S/N)": "GS30",
  "GS99 - ACCIDENTES - CAPITAL": "GS99",
  "GS09 - INDEMNIZACION HOSPITALARIA - CAPITAL": "GS09",
  "REPRESENTANTE LEGAL (S/N)": "hasLegalRepre",
  "RELACION REPRE-MENOR": "legalGuardian.relationship",
  "R NOMBRE": "legalGuardian.name",
  "R APELLIDO 1": "legalGuardian.lastname1",
  "R APELLIDO 2": "legalGuardian.lastname2",
  "R FECHA DE NACIMIENTO (DD/MM/YYYY)": "legalGuardian.birthDate",
  "R NACIONALIDAD": "legalGuardian.nationality",
  "R TIPO DOCU": "legalGuardian.docType",
  "R Nº DOCU": "legalGuardian.docNumber",
  "R CADUCIDAD DOCU": "legalGuardian.passportDate",
  "R PREFIJO MOVIL": "legalGuardian.prefix",
  "R MOVIL": "legalGuardian.mobilePhone",
  "R EMAIL": "legalGuardian.email",
  "R PROVINCIA": "legalGuardian.province",
  "R POBLACION": "legalGuardian.town",
  "R DOMICILIO": "legalGuardian.address",
  "R NUMERO/KM": "legalGuardian.number",
  "R PORTAL": "legalGuardian.portal",
  "R ESCALERA": "legalGuardian.stairs",
  "R PISO": "legalGuardian.floor",
  "R PUERTA": "legalGuardian.door",
  "R CODIGO POSTAL": "legalGuardian.postalCode",
};

export const hiringInsuredFields: HiringInsuredFieldsMap = {
  name: "NOMBRE",
  lastname1: "APELLIDO 1",
  lastname2: "APELLIDO 2",
  birthDate: "FECHA DE NACIMIENTO (DD/MM/YYYY)",
  gender: "GENERO (M/H)",
  nationality: "NACIONALIDAD",
  docType: "TIPO DOCU (NIF-NIE-PAS)",
  docNumber: "Nº DE DOCU",
  passportDate: "CADUCIDAD",
  province: "PROVINCIA",
  town: "POBLACION",
  address: "DOMICILIO",
  number: "NUMERO/KM",
  portal: "PORTAL",
  stairs: "ESCALERA",
  floor: "PISO",
  door: "PUERTA",
  postalCode: "CODIGO POSTAL",
  prefix: "PREFIJO MOVIL",
  mobilePhone: "MOVIL",
  email: "EMAIL",
  copay: "CUENTA COPAGO",
  GS30: "GS30 - DENTAL - (S/N)",
  GS99: "GS99 - ACCIDENTES - CAPITAL",
  GS09: "GS09 - INDEMNIZACION HOSPITALARIA - CAPITAL",
  hasLegalRepre: "REPRESENTANTE LEGAL (S/N)",
  legalGuardian: {
    relationship: "RELACION REPRE-MENOR",
    name: "R NOMBRE",
    lastname1: "R APELLIDO 1",
    lastname2: "R APELLIDO 2",
    birthDate: "R FECHA DE NACIMIENTO (DD/MM/YYYY)",
    nationality: "R NACIONALIDAD",
    docType: "R TIPO DOCU",
    docNumber: "R Nº DOCU",
    passportDate: "R CADUCIDAD DOCU",
    prefix: "R PREFIJO MOVIL",
    mobilePhone: "R MOVIL",
    email: "R EMAIL",
    province: "R PROVINCIA",
    town: "R POBLACION",
    address: "R DOMICILIO",
    number: "R NUMERO/KM",
    portal: "R PORTAL",
    stairs: "R ESCALERA",
    floor: "R PISO",
    door: "R PUERTA",
    postalCode: "R CODIGO POSTAL",
  },
};

const getNationalityLabel = (nationalityValue: string, countries: Country[]) => {
  const nationality = countries.find((item) => item.value === nationalityValue);
  return (nationality && nationality.label) || "";
};

const getRelationshipLabel = (relationshipValue: string) => {
  const relationship = RELACIONES_REPRESENTANTE.find((item) => item.value === relationshipValue);
  return (relationship && relationship.label) || "";
}

export const formatInsuredsCsv = (insuredData: InsuredData[], countries: Country[]) => {
  const headers = Object.keys(hiringColumnNames);
  const rows = insuredData.map((insured) => {
    const row: { [key: string]: string } = {};
    row[hiringInsuredFields.name] = insured.name;
    row[hiringInsuredFields.lastname1] = insured.lastname1;
    row[hiringInsuredFields.lastname2] = insured.lastname2;
    row[hiringInsuredFields.birthDate] = insured.birthDate;
    row[hiringInsuredFields.gender] = insured.gender;
    row[hiringInsuredFields.nationality] = insured.nationalityLabel;
    row[hiringInsuredFields.docType] = insured.docType;
    row[hiringInsuredFields.docNumber] = insured.docNumber;
    row[hiringInsuredFields.passportDate] = insured.passportDate || "";
    row[hiringInsuredFields.province] = insured.address.province.value;
    row[hiringInsuredFields.town] = insured.address.town.value;
    row[hiringInsuredFields.address] = insured.address.address.label;
    row[hiringInsuredFields.number] = insured.address.number;
    row[hiringInsuredFields.portal] = insured.address.portal;
    row[hiringInsuredFields.stairs] = insured.address.stairs;
    row[hiringInsuredFields.floor] = insured.address.floor;
    row[hiringInsuredFields.door] = insured.address.door;
    row[hiringInsuredFields.postalCode] = insured.address.postalCode;
    row[hiringInsuredFields.email] = insured.email;

    const phone = insured.mobilePhone;
    const [prefix, mobilePhone] = phone.split(" ");
    row[hiringInsuredFields.prefix] = prefix;
    row[hiringInsuredFields.mobilePhone] = mobilePhone;

    insured.coverages.forEach((coverage: any) => {
      if (coverage.code === "GS30") {
        row[hiringInsuredFields.GS30] = coverage.checked ? "S" : "N";
      } else if (coverage.code === "GS99") {
        row[hiringInsuredFields.GS99] = coverage.capitalValue || "";
      } else if (coverage.code === "GS09") {
        row[hiringInsuredFields.GS09] = coverage.capitalValue || "";
      }
    });

    if (insured.copay && insured.copay.iban) {
      row[hiringInsuredFields.copay] = insured.copay.iban;
    }

    row[hiringInsuredFields.hasLegalRepre] = insured.hasLegalRepre ? "S" : "N";
    if (insured.hasLegalRepre && insured.legalGuardian) {
      row[hiringInsuredFields.legalGuardian.name] = insured.legalGuardian.name;
      row[hiringInsuredFields.legalGuardian.lastname1] = insured.legalGuardian.lastname1;
      row[hiringInsuredFields.legalGuardian.lastname2] = insured.legalGuardian.lastname2 || "";
      row[hiringInsuredFields.legalGuardian.birthDate] = insured.legalGuardian.birthDate;
      row[hiringInsuredFields.legalGuardian.nationality] = getNationalityLabel(
        insured.legalGuardian.nationality,
        countries
      );
      row[hiringInsuredFields.legalGuardian.docType] = insured.legalGuardian.docType;
      row[hiringInsuredFields.legalGuardian.docNumber] = insured.legalGuardian.docNumber;
      row[hiringInsuredFields.legalGuardian.passportDate] =
        insured.legalGuardian.passportDate || "";
      row[hiringInsuredFields.legalGuardian.relationship] = getRelationshipLabel(insured.legalGuardian.relationship);

      row[hiringInsuredFields.legalGuardian.email] = insured.legalGuardian.email;

      const reprePhone = insured.legalGuardian.mobilePhone;
      const [prefix, mobilePhone] = reprePhone.split(" ");
      row[hiringInsuredFields.legalGuardian.prefix] = prefix;
      row[hiringInsuredFields.legalGuardian.mobilePhone] = mobilePhone;

      row[hiringInsuredFields.legalGuardian.province] =
        insured.legalGuardian.address.province.value;
      row[hiringInsuredFields.legalGuardian.town] = insured.legalGuardian.address.town.value;
      row[hiringInsuredFields.legalGuardian.address] = insured.legalGuardian.address.address.label;
      row[hiringInsuredFields.legalGuardian.number] = insured.legalGuardian.address.number;
      row[hiringInsuredFields.legalGuardian.portal] = insured.legalGuardian.address.portal || "";
      row[hiringInsuredFields.legalGuardian.stairs] = insured.legalGuardian.address.stairs || "";
      row[hiringInsuredFields.legalGuardian.floor] = insured.legalGuardian.address.floor || "";
      row[hiringInsuredFields.legalGuardian.door] = insured.legalGuardian.address.door || "";
      row[hiringInsuredFields.legalGuardian.postalCode] = insured.legalGuardian.address.postalCode;
    }

    return row;
  });

  const EOL = "\r\n";
  const SEP = ";";
  const BOM = "\uFEFF";

  const csvContent =
    BOM +
    headers.join(SEP) +
    (rows.length ? EOL : "") +
    rows
      .map((row: any) =>
        headers.map((h) => `"${String(row[h] ? row[h] : "").replace(/"/g, '""')}"`).join(SEP)
      )
      .join(EOL);

  return csvContent;
};
